import React from 'react';
type LogoVariant = 'full' | 'icon' | 'wordmark';
type LogoColorScheme = 'dark' | 'light' | 'mono-dark' | 'mono-light';
interface NexyomLogoProps {
  variant?: LogoVariant;
  colorScheme?: LogoColorScheme;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}
const sizeMap = {
  xs: {
    icon: 24,
    text: 14,
    gap: 6
  },
  sm: {
    icon: 32,
    text: 18,
    gap: 8
  },
  md: {
    icon: 48,
    text: 28,
    gap: 12
  },
  lg: {
    icon: 64,
    text: 36,
    gap: 16
  },
  xl: {
    icon: 96,
    text: 54,
    gap: 20
  }
};
const colorSchemes = {
  dark: {
    primary: '#4A90E2',
    secondary: '#6BA3E8',
    tertiary: '#8BB6EE',
    text: '#FFFFFF'
  },
  light: {
    primary: '#4A90E2',
    secondary: '#3A7BC8',
    tertiary: '#2A66AE',
    text: '#0A0A0A'
  },
  'mono-dark': {
    primary: '#FFFFFF',
    secondary: '#E0E0E0',
    tertiary: '#C0C0C0',
    text: '#FFFFFF'
  },
  'mono-light': {
    primary: '#0A0A0A',
    secondary: '#2A2A2A',
    tertiary: '#4A4A4A',
    text: '#0A0A0A'
  }
};
function LogoIcon({
  size,
  colors
}: {
  size: number;
  colors: typeof colorSchemes.dark;
}) {
  return <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" data-id="element-84">
      {/* Three ascending pillars forming abstract 'N' shape */}
      {/* Left pillar - shortest */}
      <rect x="8" y="28" width="12" height="28" rx="2" fill={colors.tertiary} data-id="element-85" />
      {/* Middle pillar - medium */}
      <rect x="26" y="18" width="12" height="38" rx="2" fill={colors.secondary} data-id="element-86" />
      {/* Right pillar - tallest */}
      <rect x="44" y="8" width="12" height="48" rx="2" fill={colors.primary} data-id="element-87" />
      {/* Connecting diagonal line (the 'N' stroke) */}
      <path d="M14 32L50 12" stroke={colors.primary} strokeWidth="4" strokeLinecap="round" data-id="element-88" />
    </svg>;
}
function LogoWordmark({
  fontSize,
  color
}: {
  fontSize: number;
  color: string;
}) {
  return <span style={{
    fontSize: `${fontSize}px`,
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color,
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  }} data-id="element-89">
      nexyom
    </span>;
}
export function NexyomLogo({
  variant = 'full',
  colorScheme = 'dark',
  size = 'md',
  className = ''
}: NexyomLogoProps) {
  const dimensions = sizeMap[size];
  const colors = colorSchemes[colorScheme];
  if (variant === 'icon') {
    return <div className={`inline-flex items-center ${className}`} role="img" aria-label="Nexyom logo" data-id="element-90">
        <LogoIcon size={dimensions.icon} colors={colors} data-id="element-91" />
      </div>;
  }
  if (variant === 'wordmark') {
    return <div className={`inline-flex items-center ${className}`} role="img" aria-label="Nexyom" data-id="element-92">
        <LogoWordmark fontSize={dimensions.text} color={colors.text} data-id="element-93" />
      </div>;
  }
  return <div className={`inline-flex items-center ${className}`} style={{
    gap: `${dimensions.gap}px`
  }} role="img" aria-label="Nexyom logo" data-id="element-94">
      <LogoIcon size={dimensions.icon} colors={colors} data-id="element-95" />
      <LogoWordmark fontSize={dimensions.text} color={colors.text} data-id="element-96" />
    </div>;
}
// Export individual pieces for flexibility
export function NexyomIcon({
  size = 48,
  colorScheme = 'dark',
  className = ''
}: {
  size?: number;
  colorScheme?: LogoColorScheme;
  className?: string;
}) {
  const colors = colorSchemes[colorScheme];
  return <div className={`inline-flex ${className}`} role="img" aria-label="Nexyom icon" data-id="element-97">
      <LogoIcon size={size} colors={colors} data-id="element-98" />
    </div>;
}